import Modal from "@/components/controls/Modal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { periodsDropdownProps } from "@/components/layouts/WinRate/helpers";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import {
  DirectReportMonthlyData,
  SettingsPeriod
} from "@/entities/reporting/types";
import LayoutTabs from "@/components/controls/LayoutTabs";
import { useUsers } from "@/entities/users/hooks";
import { UserData } from "@/entities/users/types";
import MonthlyLeadsChart from "@/components/layouts/LeadAnalysis/MonthlyLeadChart";
import LeadAnalysisTableCardController from "@/components/layouts/LeadAnalysis/LeadAnalysisTableCardController";
import { useLeadAnalysisBreadcrumbs } from "@/components/layouts/LeadAnalysis/LeadAnalysisBreadcrumbsProvider";
import UserBreadcrumbs from "@/components/controls/UserBreadcrumbs/UserBreadcrumbs";
import { TAB_IDS, TabId } from "@/components/layouts/LeadAnalysis/types";
import {
  getDataByKPI,
  getTabOptions
} from "@/components/layouts/LeadAnalysis/helpers";
import Spinner from "@/components/ui/spinner";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
}

const LeadAnalysisDirectReportModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  period,
  periodSelect
}) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<TabId>(TAB_IDS[0]);

  const { breadcrumbIds, setSelectedUserId, directReportsData, isLoading } =
    useLeadAnalysisBreadcrumbs();

  const [chartData, setChartData] = useState<
    DirectReportMonthlyData | undefined
  >(directReportsData?.avgMonthlyQualifiedConvertedLeads);

  const overall = directReportsData?.overall;
  const usersIds = overall?.map((report) => report.id) || [];
  const { users: usersData } = useUsers(usersIds);
  const userMap = new Map<string, UserData>();
  usersData.forEach((user) => {
    userMap.set(user.id, user);
  });

  const onTabChange = (newTabValue: string) => {
    if (TAB_IDS.includes(newTabValue as TabId)) {
      const typedTabValue = newTabValue as TabId;
      setTabValue(typedTabValue);
      const newData = getDataByKPI(typedTabValue, directReportsData);
      setChartData(newData as DirectReportMonthlyData);
    }
  };

  useEffect(() => {
    if (!chartData) {
      setChartData(directReportsData?.avgMonthlyQualifiedConvertedLeads);
    }
  }, [directReportsData, chartData]);

  const tabOptions = getTabOptions(t);

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[75%]"
      title={
        <div className="flex flex-row w-full justify-between p-1">
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-col flex-nowrap items-start justify-between align-start">
              <UserBreadcrumbs
                title={t("leadAnalysis.title")}
                subtitle={t("leadAnalysis.directReport.subTitle")}
                ids={breadcrumbIds}
                setter={setSelectedUserId}
              />
            </div>
          </div>
          <div>
            <Dropdown
              {...periodsDropdownProps(t)}
              label={t("conversionRates.timeframe")}
              defaultValue={periodEnumToString(period)}
              valueSelected={(value) => periodSelect(periodStringToEnum(value))}
            />
          </div>
        </div>
      }
      content={
        isLoading ? (
          <div className="flex w-full min-h-[40rem] h-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="flex flex-col gap-10 min-h-80 h-full w-full">
            <LeadAnalysisTableCardController
              dataTestPrefix="lead-analysis"
              title={t("leadAnalysis.title")}
              subtitle={t("leadAnalysis.directReport.subTitle")}
              isLoading={false}
            />

            <LayoutTabs
              className="text-base"
              options={tabOptions}
              defaultValue={tabValue}
              valueSelected={onTabChange}
            />

            {chartData && (
              <MonthlyLeadsChart
                data={chartData}
                userMap={userMap}
                height={300}
                isPercentage={
                  tabValue === "leadQualification" ||
                  tabValue === "leadConversion"
                }
              />
            )}
          </div>
        )
      }
    />
  );
};

export default LeadAnalysisDirectReportModal;
